import React, { useEffect, useState } from 'react';
import { motion, useAnimation, Variants, easeOut, easeInOut } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Users, Building, Heart, Trophy, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';


const AboutSection: React.FC = () => {
  const [csrData, setCsrData] = useState<any>({});
  const [awardsData, setAwardsData] = useState<any>({});
  const { t_api } = useLanguage();


  const [sectionRef, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const [csrRef, csrInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const [awardsRef, awardsInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const controls = useAnimation();
  const csrControls = useAnimation();
  const awardsControls = useAnimation();

  useEffect(() => {
    fetch('/api/homepage/content')
      .then(res => res.json())
      .then(data => {
        setCsrData(data.CSR || {});
        setAwardsData(data.Awards || {});
      })
      .catch(err => console.error("Error fetching content:", err));
  }, []);

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [inView, controls]);

  useEffect(() => {
    if (csrInView) {
      csrControls.start('visible');
    }
  }, [csrInView, csrControls]);

  useEffect(() => {
    if (awardsInView) {
      awardsControls.start('visible');
    }
  }, [awardsInView, awardsControls]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: easeInOut
      }
    }
  };

  const underlineVariants = {
    hidden: { scaleX: 0 },
    visible: {
      scaleX: 1,
      transition: {
        duration: 0.6,
        ease: easeInOut,
        delay: 0.2
      }
    }
  };

  const draw: Variants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: (i: number) => ({
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: { delay: 0.5 + i * 0.3, type: "spring", duration: 1, bounce: 0 },
        opacity: { delay: 0.5 + i * 0.3, duration: 0.3 },
      },
    }),
    hover: {
      pathLength: 1.02,
      strokeWidth: 1.2,
      transition: { duration: 0.3 },
    },
  };

  const csrContent = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: t_api(csrData?.point_1_title || "Community Commitment", csrData?.point_1_title_ar),
      description: t_api(csrData?.point_1_desc || "Hamdan Trading Group (HTG) is committed to running its business in a socially responsible manner. The group prides itself as being a part of the Omani society and strives to maintain their dealings with high values and standards keeping up with the culture of the region.", csrData?.point_1_desc_ar)
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: t_api(csrData?.point_2_title || "Excellence Since 1975", csrData?.point_2_title_ar),
      description: t_api(csrData?.point_2_desc || "Since 1975, the group has been providing excellent services and support to the community of the sultanate of Oman. Through the wide spectrum services offered by its sub divisions, Hamdan Trading Group has efficiently managed to play a proactive part in the society.", csrData?.point_2_desc_ar)
    },
    {
      icon: <Building className="w-6 h-6" />,
      title: t_api(csrData?.point_3_title || "Landmark Development", csrData?.point_3_title_ar),
      description: t_api(csrData?.point_3_desc || "The Hamdan Building located in Salalah is a testament to the dedication of Hamdan Trading Group towards the region of Dhofar. The Building houses a Hospital, hotel and Money Exchange apart from the divisions related to Construction, Transport and Dunlop Tyres.", csrData?.point_3_desc_ar)
    }
  ];

  const achievements = [
    {
      icon: <Trophy className="w-6 h-6" />,
      title: t_api(awardsData?.point_1_title || "Quality Excellence", awardsData?.point_1_title_ar),
      description: t_api(awardsData?.point_1_desc || "Since the inception of Hamdan Trading Group in 1975, the organization has taken into custom of delivering products and services of the highest quality and has therefore received a multitude of awards from the government sector as well as private establishments.", awardsData?.point_1_desc_ar)
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: t_api(awardsData?.point_2_title || "Global Recognition", awardsData?.point_2_title_ar),
      description: t_api(awardsData?.point_2_desc || "These awards have been presented to them by organizations and clients locally as well as internationally. Among others, the notable awards have been received from Petroleum Development Oman, Diwan of Royal Court as well as an award of appreciation from the American Embassy.", awardsData?.point_2_desc_ar)
    }
  ];

  return (
    <section ref={sectionRef} className="py-10 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* CSR Section */}
        <motion.div
          ref={csrRef}
          className="mb-16"
          variants={containerVariants}
          initial="hidden"
          animate={csrControls}
        >
          <motion.div variants={itemVariants} className="mb-8">
            <h2 className="text-3xl md:text-3xl font-bold text-dark text-center">
              {t_api(csrData?.title || "Corporate Social Responsibility", csrData?.title_ar)}
            </h2>
            <motion.div
              className="w-20 h-1 mx-auto rounded mt-4"
              style={{ backgroundColor: '#F2BB13', transformOrigin: 'center' }}
              variants={underlineVariants}
            />
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mt-8">
            {csrContent.map((item, index) => (
              <motion.div
                key={index}
                className="relative group h-full"
                initial="hidden"
                animate={csrInView ? "visible" : "hidden"}
                custom={index}
                whileHover="hover"
                variants={itemVariants}
              >
                {/* Animated Golden Border */}
                <motion.svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 200 100"
                  preserveAspectRatio="none"
                  className="absolute inset-0 z-30 pointer-events-none"
                >
                  <motion.rect
                    x="2"
                    y="2"
                    width="196"
                    height="96"
                    rx="4"
                    stroke="#F2BB13"
                    strokeWidth="0.8"
                    fill="transparent"
                    variants={draw as any}
                    custom={index}
                  />
                </motion.svg>

                <div className="p-6 bg-white rounded-lg relative z-10 m-1 flex flex-col h-full min-h-[300px]">
                  <div
                    className="inline-flex p-4 rounded-lg mb-6 text-dark transition-all duration-300 group-hover:scale-110 w-fit"
                    style={{ backgroundColor: '#F2BB13' }}
                  >
                    {item.icon}
                  </div>

                  <h3 className="text-xl font-bold text-dark mb-4 group-hover:text-gray-700 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-justify flex-1">
                    {item.description}
                  </p>

                  <div
                    className="w-0 h-1 mt-6 rounded transition-all duration-300 group-hover:w-full"
                    style={{ backgroundColor: '#F2BB13' }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Awards Section */}
        <motion.div
          ref={awardsRef}
          variants={containerVariants}
          initial="hidden"
          animate={awardsControls}
        >
          <motion.div variants={itemVariants} className="mb-8">
            <h2 className="text-3xl md:text-3xl font-bold text-dark text-center">
              {t_api(awardsData?.title || "Awards & Achievements", awardsData?.title_ar)}
            </h2>
            <motion.div
              className="w-20 h-1 mx-auto rounded mt-4"
              style={{ backgroundColor: '#F2BB13', transformOrigin: 'center' }}
              variants={underlineVariants}
            />
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mt-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                className="relative group h-full flex"
                initial="hidden"
                animate={awardsInView ? "visible" : "hidden"}
                custom={index}
                whileHover="hover"
                variants={itemVariants}
              >
                {/* Animated Golden Border */}
                <motion.svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 200 100"
                  preserveAspectRatio="none"
                  className="absolute inset-0 z-30 pointer-events-none"
                >
                  <motion.rect
                    x="2"
                    y="2"
                    width="196"
                    height="96"
                    rx="4"
                    stroke="#F2BB13"
                    strokeWidth="0.8"
                    fill="transparent"
                    variants={draw as any}
                    custom={index}
                  />
                </motion.svg>

                {/* Inner card with equal height */}
                <div className="p-8 bg-white rounded-2xl relative z-10 m-1 flex flex-col justify-between w-full min-h-[340px]">
                  <div className="flex items-center gap-4 mb-6">
                    <div
                      className="p-4 rounded-lg text-dark transition-all duration-300 group-hover:scale-110 flex-shrink-0"
                      style={{ backgroundColor: '#F2BB13' }}
                    >
                      {achievement.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-dark group-hover:text-gray-700 transition-colors">
                      {achievement.title}
                    </h3>
                  </div>

                  <p className="text-gray-600 leading-relaxed text-justify flex-1">
                    {achievement.description}
                  </p>

                  <div
                    className="w-0 h-1 mt-6 rounded transition-all duration-300 group-hover:w-full"
                    style={{ backgroundColor: '#F2BB13' }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;